import {AfterViewInit, ChangeDetectionStrategy, Component, inject, input, InputSignal, OnDestroy, signal, Signal, viewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {AppService} from '../../services/app/app.service';
import {VaultQuery} from '../../queries/vault.query';
import {Vault} from '../../api/models/vault';
import {AmagnoLinkService} from '../../services/amagno-link/amagno-link.service';
import {environment} from '../../../environments/environment';
import {BasicSubscribableComponent} from '../dummy-components/basic-subscribable-component';
import {SearchService} from '../../services/search/search.service';
import {StoredSearchesComponent} from '../stored-searches/stored-searches.component';
import {ClickDirective} from '../../directives/click.directive';
import {TranslateModule} from '@ngx-translate/core';
import {LoadingSpinnerComponent} from '../loading-spinner/loading-spinner.component';
import {isGuid} from '../../util/is-guid/is-guid.util';
import {IconComponent} from '../icon/icon.component';
import {AppIconSizeEnum} from '../../enums/app-icon-size.enum';
import {RecentSearchService} from '../../services/search/recent-search/recent-search.service';
import {RecentSearch} from '../../models/search/recent-search.model';
import {TextInputComponent} from '../form-elements/text-input/text-input.component';
import {ContentPrefixDirective} from '../../directives/content-prefix.directive';
import {ThemeService} from '../../services/theme/theme.service';
import {toSignal} from '@angular/core/rxjs-interop';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-search-dashboard',
    templateUrl: './search-dashboard.component.html',
    styleUrls: ['./search-dashboard.component.css'],
    imports: [
        ReactiveFormsModule,
        StoredSearchesComponent,
        ClickDirective,
        TranslateModule,
        LoadingSpinnerComponent,
        IconComponent,
        TextInputComponent,
        ContentPrefixDirective
    ]
})
export class SearchDashboardComponent extends BasicSubscribableComponent implements AfterViewInit, OnDestroy {
    public isFocusInputDisabled: InputSignal<boolean> = input<boolean>(true);
    public vaultId: InputSignal<string | undefined> = input();
    public isGlobalSearchView: InputSignal<boolean> = input(true);
    protected form: FormGroup;
    protected recentSearches: Signal<Array<RecentSearch>>;
    protected isShowingLoadingSpinner = signal<boolean>(false);
    protected isDarkMode: Signal<boolean>;
    protected readonly AppIconSize = AppIconSizeEnum;
    private readonly searchInput: Signal<TextInputComponent | undefined> = viewChild<TextInputComponent>('searchInput');
    private readonly activeVault: Signal<Vault | undefined>;
    private readonly amagnoLinkService = inject(AmagnoLinkService);
    private readonly appService = inject(AppService);
    private readonly formBuilder = inject(FormBuilder);
    private readonly recentSearchService = inject(RecentSearchService);
    private readonly searchService = inject(SearchService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly themeService = inject(ThemeService);

    public constructor() {
        super();
        this.form = this.formBuilder.group({
            keyword: new FormControl<string | null>(''),
        });
        this.recentSearches = this.recentSearchService.getRecentSearchesSignal();
        this.isDarkMode = this.themeService.getIsDarkModeSignal();
        this.activeVault = toSignal(this.vaultQuery.activeVault$);
    }

    public ngAfterViewInit(): void {
        if (!this.isFocusInputDisabled()) {
            this.focusInput();
        }
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        this.blurInput();
    }

    protected getVaultNameByVaultId(vaultId: string): string {
        return this.vaultQuery.getVaultNameById(vaultId);
    }

    protected async directSearch(keyword: string): Promise<void> {
        await this.executeSearch(keyword);
    }

    protected async onSubmit(): Promise<void> {
        this.isShowingLoadingSpinner.set(true);

        const keyword = this.form.value.keyword as string;
        if (keyword) {
            this.clearInput();
            this.blurInput();
            const keywordWithoutUrl = keyword.replace(new RegExp('url:' + environment.deepLinkPrefix), environment.deepLinkPrefix);
            await this.executeSearch(keywordWithoutUrl);
        }

        this.isShowingLoadingSpinner.set(false);
    }

    protected fillInputWithRecentSearch(keyword: string | undefined): void {
        this.form.patchValue({ keyword });
        this.focusInput();
    }

    private async amagnoSearch(keyword: string): Promise<void> {
        await this.searchService.openSearch(keyword, this.isGlobalSearchView() ? undefined : this.activeVault());
    }

    private clearInput(): void {
        this.form.patchValue({ keyword: '' });
    }

    private focusInput(): void {
        this.searchInput()
            ?.focus();
    }

    private blurInput(): void {
        this.searchInput()
            ?.blur();
    }

    private async executeSearch(keyword: string): Promise<void> {
        if (keyword && (keyword.startsWith(environment.deepLinkPrefix) || keyword.startsWith('url:' + environment.deepLinkPrefix))) {
            if (await this.amagnoLinkService.openAmagnoLinkByAmagnoDeepLink(keyword)) {
                this.appService.hideSearchMenu();
            }

            return;
        }

        if (isGuid(keyword)) {
            if (await this.amagnoLinkService.openAmagnoLinkByDocumentId(keyword)) {
                this.appService.hideSearchMenu();
            }

            return;
        }

        await this.amagnoSearch(keyword);
    }
}
